export const ACCOUNT_NUMBER_DIGIT_MAX = 2000;
export const ACCOUNT_SEGMENT_COUNT_MIN = 1;
export const ACCOUNT_SEGMENT_COUNT_MAX = 20;
export const DEFAULT_ACCOUNT_SEPARATOR = '-';

const PLACEHOLDER_DIGIT = '0';

export type AccountNumberEdit = {
  values: string[];
  caret: number;
};

/** Máscara visible. Un segmento vacío se muestra como `0` y no queda capturado. */
export function maskAccount(
  values: readonly string[],
  separator = DEFAULT_ACCOUNT_SEPARATOR,
): string {
  return values.map(value => value || PLACEHOLDER_DIGIT).join(separator);
}

export function emptyAccountSegments(count: number): string[] {
  const size = Number.isInteger(count) && count > 0 ? count : 0;
  return Array.from({ length: size }, () => '');
}

/**
 * Arma el `accountNumber` con el separador de la configuración.
 * Un segmento vacío sigue siendo placeholder y no se envía.
 */
export function accountNumberFromSegments(
  values: readonly string[],
  separator: string,
): string | null {
  if (accountNumberIssue(values, separator)) return null;
  return values.join(separator);
}

export function accountNumberIssue(
  values: readonly string[],
  separator: string,
): string | null {
  if (!separator || values.length < ACCOUNT_SEGMENT_COUNT_MIN) {
    return 'Configura la estructura de la cuenta antes de capturar el número.';
  }
  if (values.some(value => value.length === 0)) {
    return 'Captura cada segmento de la cuenta.';
  }
  if (values.some(value => !/^\d+$/.test(value))) {
    return 'Cada segmento solo admite dígitos.';
  }
  if (digitCount(values) > ACCOUNT_NUMBER_DIGIT_MAX) {
    return 'El número de cuenta admite hasta 2000 dígitos.';
  }
  return null;
}

/**
 * Parte `formattedAccountNumber` con el separador publicado.
 * Devuelve null si la cantidad de segmentos no coincide: no se infiere el corte.
 */
export function parseFormattedAccountNumber(
  formatted: string,
  separator: string,
  segmentCount: number,
): string[] | null {
  if (!separator || !Number.isInteger(segmentCount)) return null;
  if (
    segmentCount < ACCOUNT_SEGMENT_COUNT_MIN ||
    segmentCount > ACCOUNT_SEGMENT_COUNT_MAX
  ) {
    return null;
  }
  const parts = formatted.split(separator);
  if (parts.length !== segmentCount) return null;
  if (parts.some(part => !/^\d+$/.test(part))) return null;
  if (digitCount(parts) > ACCOUNT_NUMBER_DIGIT_MAX) return null;
  return parts;
}

export function applyAccountEdit(
  previous: readonly string[],
  previousDisplay: string,
  nextValue: string,
  cursor: number,
  separator = DEFAULT_ACCOUNT_SEPARATOR,
): AccountNumberEdit {
  if (nextValue.length === previousDisplay.length + 1) {
    const insertAt = cursor > 0 ? cursor - 1 : 0;
    return insertAccountChar(
      previous,
      insertAt,
      nextValue.charAt(insertAt),
      separator,
    );
  }

  if (nextValue.length === previousDisplay.length - 1) {
    return deleteAccountChar(previous, previousDisplay, nextValue, separator);
  }

  if (nextValue.length === previousDisplay.length) {
    return replaceAccountChar(
      previous,
      previousDisplay,
      nextValue,
      cursor,
      separator,
    );
  }

  const values = reconcileAccountSegments(previous, nextValue, separator);
  return {
    values,
    caret: Math.min(cursor, maskAccount(values, separator).length),
  };
}

function digitCount(values: readonly string[]): number {
  return values.reduce((total, value) => total + value.length, 0);
}

function segmentBounds(
  values: readonly string[],
  separator: string,
): { start: number; end: number }[] {
  const bounds: { start: number; end: number }[] = [];
  let start = 0;
  for (const value of values) {
    const length = value.length === 0 ? 1 : value.length;
    bounds.push({ start, end: start + length });
    start += length + separator.length;
  }
  return bounds;
}

function locateSegment(
  values: readonly string[],
  index: number,
  separator: string,
): { index: number; offset: number } {
  const bounds = segmentBounds(values, separator);
  for (let i = 0; i < bounds.length; i++) {
    const bound = bounds[i];
    if (index <= bound.end || i === bounds.length - 1) {
      const storedLength = values[i]?.length ?? 0;
      const offset =
        storedLength === 0
          ? 0
          : Math.min(Math.max(index - bound.start, 0), storedLength);
      return { index: i, offset };
    }
  }
  return { index: 0, offset: 0 };
}

function firstDiff(previous: string, next: string): number {
  const max = Math.max(previous.length, next.length);
  for (let index = 0; index < max; index++) {
    if (previous[index] !== next[index]) return index;
  }
  return -1;
}

function insertAccountChar(
  previous: readonly string[],
  insertAt: number,
  char: string,
  separator: string,
): AccountNumberEdit {
  const bounds = segmentBounds(previous, separator);
  const place = locateSegment(previous, insertAt, separator);
  const currentBound = bounds[place.index];

  if (separator.length === 1 && char === separator) {
    const hasNext = place.index < previous.length - 1;
    return {
      values: [...previous],
      caret: hasNext ? bounds[place.index + 1].start : (currentBound?.end ?? 0),
    };
  }

  if (!/^\d$/.test(char) || !currentBound) {
    return { values: [...previous], caret: currentBound?.start ?? 0 };
  }

  if (digitCount(previous) >= ACCOUNT_NUMBER_DIGIT_MAX) {
    return { values: [...previous], caret: currentBound.start };
  }

  const values = [...previous];
  if (values[place.index] === '') {
    values[place.index] = char;
    return { values, caret: currentBound.start + 1 };
  }

  const offset = place.offset;
  values[place.index] =
    values[place.index].slice(0, offset) + char + values[place.index].slice(offset);
  return { values, caret: currentBound.start + offset + 1 };
}

function deleteAccountChar(
  previous: readonly string[],
  previousDisplay: string,
  nextValue: string,
  separator: string,
): AccountNumberEdit {
  const deleteAt = firstDiff(previousDisplay, nextValue);
  const bounds = segmentBounds(previous, separator);
  const place = locateSegment(previous, Math.max(deleteAt, 0), separator);
  const bound = bounds[place.index];
  const removed = previousDisplay.charAt(deleteAt);

  if (!/^\d$/.test(removed) || !bound) {
    return { values: [...previous], caret: Math.max(deleteAt, 0) };
  }

  const current = previous[place.index] ?? '';
  if (current === '') {
    return { values: [...previous], caret: bound.start };
  }

  const offset = Math.min(Math.max(deleteAt - bound.start, 0), current.length - 1);
  const values = [...previous];
  values[place.index] = current.slice(0, offset) + current.slice(offset + 1);
  return { values, caret: bound.start + offset };
}

function replaceAccountChar(
  previous: readonly string[],
  previousDisplay: string,
  nextValue: string,
  cursor: number,
  separator: string,
): AccountNumberEdit {
  const index = firstDiff(previousDisplay, nextValue);
  if (index < 0) return { values: [...previous], caret: cursor };

  const char = nextValue.charAt(index);
  const removed = previousDisplay.charAt(index);
  if (
    (separator.length === 1 && (char === separator || removed === separator)) ||
    !/^\d$/.test(char)
  ) {
    return insertAccountChar(
      previous,
      index,
      separator.length === 1 && char === separator ? separator : char,
      separator,
    );
  }

  const bounds = segmentBounds(previous, separator);
  const place = locateSegment(previous, index, separator);
  const values = [...previous];
  if (values[place.index] === '') {
    if (digitCount(previous) >= ACCOUNT_NUMBER_DIGIT_MAX) {
      return { values: [...previous], caret: bounds[place.index]?.start ?? 0 };
    }
    values[place.index] = char;
    return { values, caret: bounds[place.index].start + 1 };
  }

  const offset = Math.min(
    Math.max(index - bounds[place.index].start, 0),
    values[place.index].length - 1,
  );
  const current = values[place.index];
  values[place.index] = current.slice(0, offset) + char + current.slice(offset + 1);
  return { values, caret: bounds[place.index].start + offset + 1 };
}

function reconcileAccountSegments(
  previous: readonly string[],
  nextValue: string,
  separator: string,
): string[] {
  const raw = nextValue.split(separator).map(part => part.replace(/\D/g, ''));
  const parts = raw.slice(0, previous.length);
  while (parts.length < previous.length) parts.push('');

  let used = 0;
  return parts.map((part, index) => {
    const normalized =
      (previous[index] ?? '') === '' && part === PLACEHOLDER_DIGIT ? '' : part;
    const room = ACCOUNT_NUMBER_DIGIT_MAX - used;
    const kept = normalized.slice(0, Math.max(room, 0));
    used += kept.length;
    return kept;
  });
}
